import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { WireframeBackground } from '@/components/WireframeBackground';
import { MeshpitLogo } from '@/components/MeshpitLogo';
import { FadeIn } from './features/FadeIn';
import { FeatureSection } from './features/FeatureSection';
import { FEATURE_GROUPS } from './features/index';

export function Features() {
  return (
    <div className="relative min-h-full bg-background">
      <WireframeBackground />
      <div className="relative z-10 mx-auto max-w-6xl px-6 pb-32">
        {/* ---------------------------------------------------------------- */}
        {/* HEADER                                                            */}
        {/* ---------------------------------------------------------------- */}
        <div className="py-16">
          <FadeIn>
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to home
            </Link>
          </FadeIn>

          <FadeIn delay={0.04}>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
              Everything Meshpit can do
            </p>
          </FadeIn>

          <FadeIn delay={0.08}>
            <h1 className="max-w-2xl text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              Features
            </h1>
          </FadeIn>

          <FadeIn delay={0.13}>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground">
              A complete reference of what Meshpit offers — from the parameter
              control system to AI tools, textures, publishing, and everything
              in between.
            </p>
          </FadeIn>

          {/* Jump-to links */}
          <FadeIn delay={0.18}>
            <nav
              className="mt-8 flex flex-wrap gap-2"
              aria-label="Jump to section"
            >
              {FEATURE_GROUPS.map((g) => (
                <a
                  key={g.id}
                  href={`#${g.id}`}
                  className="inline-flex items-center rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/20 hover:bg-muted hover:text-foreground"
                >
                  {g.label}
                </a>
              ))}
            </nav>
          </FadeIn>
        </div>

        <FadeIn>
          <Separator />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* FEATURE SECTIONS                                                  */}
        {/* ---------------------------------------------------------------- */}
        {FEATURE_GROUPS.map((group, index) => (
          <>
            <FeatureSection key={group.id} group={group} />
            {index < FEATURE_GROUPS.length - 1 && (
              <FadeIn key={`sep-${group.id}`}>
                <Separator />
              </FadeIn>
            )}
          </>
        ))}

        {/* ---------------------------------------------------------------- */}
        {/* CTA FOOTER                                                        */}
        {/* ---------------------------------------------------------------- */}
        <FadeIn>
          <Separator className="mb-16" />
        </FadeIn>

        <section className="flex flex-col items-center py-16 text-center">
          <FadeIn>
            <div className="mb-6 flex items-center justify-center">
              <MeshpitLogo className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Ready to try it all?
            </h2>
            <p className="mt-4 max-w-md text-base text-muted-foreground">
              Everything above runs entirely in your browser. Open a blank file
              and start exploring — no installation required.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/new">
                <Button size="lg" className="gap-2 px-7">
                  Start Creating
                  <span aria-hidden>→</span>
                </Button>
              </Link>
              <Link to="/explore">
                <Button size="lg" variant="outline" className="gap-2 px-7">
                  Explore Models
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-xs text-muted-foreground/60">
              <Link
                to="/"
                className="underline-offset-4 hover:text-muted-foreground/90 hover:underline"
              >
                Home
              </Link>
              {' · '}
              <Link
                to="/changelog"
                className="underline-offset-4 hover:text-muted-foreground/90 hover:underline"
              >
                Changelog
              </Link>
              {' · '}
              <Link
                to="/blog"
                className="underline-offset-4 hover:text-muted-foreground/90 hover:underline"
              >
                Blog
              </Link>
            </p>
          </FadeIn>
        </section>
      </div>
    </div>
  );
}
